import { Building2, CheckSquare, FolderKanban, Hash, Lock, Route, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { MentionKind } from '@tradeoutlet/shared';
import { useWorkspaceId } from '@/lib/hooks';
import { useMentionItem } from '@/lib/entityMentions';
import { useUi } from '@/lib/store';

export const MENTION_ICON: Record<MentionKind, typeof Hash> = { building: Building2, task: CheckSquare, project: FolderKanban, journey: Route, issue: TriangleAlert, channel: Hash };

export function mentionHref(kind: MentionKind, id: string): string | null {
  switch (kind) {
    case 'building': return `/buildings/${id}`;
    case 'project': return `/projects/${id}`;
    case 'journey': return `/fleet/journeys/${id}`;
    case 'issue': return `/incidents/${id}`;
    case 'channel': return `/c/${id}`;
    default: return null;
  }
}

/** t121: a # mention in a message. Shows the item only if you could open it yourself. */
export function EntityChip({ kind, id }: { kind: MentionKind; id: string }) {
  const ws = useWorkspaceId();
  const q = useMentionItem(ws, kind, id);
  const nav = useNavigate();
  const openTask = useUi(s => s.openTask);
  const Icon = MENTION_ICON[kind];
  const cls = 'mx-0.5 inline-flex max-w-[22rem] items-center gap-1 rounded-md border px-1.5 align-baseline text-[0.9em] leading-snug';
  if (q.isLoading) return <span className={`${cls} border-slate-200 text-slate-400 dark:border-slate-700`}><Icon className="h-3 w-3 shrink-0" />…</span>;
  const it = q.data;
  if (!it) return <span className={`${cls} border-dashed border-slate-300 text-slate-500 dark:border-slate-600`} title="You don't have access to this, or it was removed" data-testid="entity-chip-hidden"><Lock className="h-3 w-3 shrink-0" />Private {kind}</span>;
  const open = () => { if (kind === 'task') openTask(id); else { const h = mentionHref(kind, id); if (h) nav(h); } };
  return (
    <button type="button" onClick={open} data-testid="entity-chip" data-kind={kind} title={it.sub ? `${it.label} · ${it.sub}` : it.label}
      className={`${cls} border-slate-300 bg-slate-50 font-medium text-slate-800 hover:border-black hover:bg-accent hover:text-on-accent dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100`}>
      <Icon className="h-3 w-3 shrink-0" /><span className="truncate">{it.label.replace(/^#/, '')}</span>
    </button>
  );
}
